// src/pages/sessions/components/LiveSession/hooks/useMediaControls.jsx
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Track } from 'livekit-client';
import sessionService from '@/services/sessionService';
import { toast } from 'sonner';

export const useMediaControls = (roomRef, isTeacher, sessionId, wsSendMessage) => {
  const { t } = useTranslation();
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [videoToggling, setVideoToggling] = useState(false);
  const [cameraLockEnabled, setCameraLockEnabled] = useState(false);
  const [microphoneLockEnabled, setMicrophoneLockEnabled] = useState(false);
  
  // Track user-initiated mutes
  const [userMutedAudio, setUserMutedAudio] = useState(false);
  const [userMutedVideo, setUserMutedVideo] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState({
    audio: false,
    video: false
  });
  
  // Track mounted state and initialization
  const mountedRef = useRef(true);
  const cleanupFunctionsRef = useRef([]);
  const hasInitializedRef = useRef(false);
  
  // Abort controller for API calls
  const abortControllerRef = useRef(null);

  // ADDED: Refs to track current values and avoid stale closures
  const videoEnabledRef = useRef(videoEnabled);
  const audioEnabledRef = useRef(audioEnabled);
  
  // ADDED: Keep refs in sync with state
  useEffect(() => {
    videoEnabledRef.current = videoEnabled;
    audioEnabledRef.current = audioEnabled;
  }, [videoEnabled, audioEnabled]);

  const isTeacherUser = typeof isTeacher === 'function' ? isTeacher() : isTeacher;
  
  const canToggleCamera = isTeacherUser || !cameraLockEnabled;
  const canToggleMicrophone = isTeacherUser || !microphoneLockEnabled;

  // Load media restrictions on mount
  useEffect(() => {
    if (!sessionId) return;
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    const loadRestrictions = async () => {
      try {
        const response = await sessionService.getMediaRestrictions(
          sessionId,
          { signal: abortController.signal }
        );
        
        if (!mountedRef.current) return;
        
        if (response?.success && response?.data) {
          setCameraLockEnabled(response.data.cameraLocked || false);
          setMicrophoneLockEnabled(response.data.microphoneLocked || false);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.debug('No media restrictions found');
        }
      }
    };
    
    loadRestrictions();
    
    return () => {
      abortController.abort();
    };
  }, [sessionId]);

  // Direct initialization effect
  useEffect(() => {
    const room = roomRef.current;
    const localParticipant = room?.localParticipant;
    
    if (!localParticipant || hasInitializedRef.current) {
      return;
    }
    
    // Mark as initialized
    hasInitializedRef.current = true;
    
    const micPub = Array.from(localParticipant.audioTrackPublications.values())
      .find(pub => pub.source === Track.Source.Microphone);
    const cameraPub = Array.from(localParticipant.videoTrackPublications.values())
      .find(pub => pub.source === Track.Source.Camera);
    
    const actualAudioState = micPub ? !micPub.isMuted : true;
    const actualVideoState = cameraPub ? !cameraPub.isMuted : true;
    
    setAudioEnabled(actualAudioState);
    setVideoEnabled(actualVideoState);
    setUserMutedAudio(!actualAudioState);
    setUserMutedVideo(!actualVideoState);
    
    // Event handlers
    const handleTrackMuted = (publication) => {
      if (!mountedRef.current) return;
      
      if (publication.source === Track.Source.Microphone) {
        setAudioEnabled(false);
        setUserMutedAudio(true);
      } else if (publication.source === Track.Source.Camera) {
        setVideoEnabled(false);
        setUserMutedVideo(true);
      }
    };
    
    const handleTrackUnmuted = (publication) => {
      if (!mountedRef.current) return;
      
      if (publication.source === Track.Source.Microphone) {
        setAudioEnabled(true);
        setUserMutedAudio(false);
      } else if (publication.source === Track.Source.Camera) {
        setVideoEnabled(true);
        setUserMutedVideo(false);
      }
    };
    
    localParticipant.on('trackMuted', handleTrackMuted);
    localParticipant.on('trackUnmuted', handleTrackUnmuted);
    
    const cleanup = () => {
      try {
        localParticipant.off('trackMuted', handleTrackMuted);
        localParticipant.off('trackUnmuted', handleTrackUnmuted);
      } catch (e) {
        console.debug('Error removing media control listeners:', e);
      }
    };
    
    cleanupFunctionsRef.current.push(cleanup);
    
    return cleanup;
  }, [roomRef.current?.localParticipant]);

  const toggleAudio = useCallback(async () => {
    if (!mountedRef.current) return;
    
    if (!canToggleMicrophone && audioEnabled) {
      toast.warning('Microphone is locked ON by the teacher');
      return;
    }
    
    const room = roomRef.current;
    if (!room?.localParticipant) return;
    
    const newState = !audioEnabled;
    
    setHasUserInteracted(prev => ({ ...prev, audio: true }));
    setAudioEnabled(newState);
    setUserMutedAudio(!newState);
    
    try {
      if (newState) {
        // When enabling, use the stored device preference
        const savedAudioDevice = localStorage.getItem('simpleclass_audio_device');
        await room.localParticipant.setMicrophoneEnabled(true, savedAudioDevice ? {
          deviceId: { exact: savedAudioDevice }
        } : undefined);
      } else {
        await room.localParticipant.setMicrophoneEnabled(false);
      }
    } catch (error) {
      console.error('Failed to toggle microphone:', error);
      
      if (mountedRef.current) {
        setAudioEnabled(!newState);
        setUserMutedAudio(newState);
        toast.error(t('liveSession.audioToggleFailed', 'Failed to toggle microphone'));
      }
    }
  }, [audioEnabled, canToggleMicrophone, roomRef]);

  const toggleVideo = useCallback(async () => {
    if (!mountedRef.current) return;
    
    if (!canToggleCamera && videoEnabled) {
      toast.warning('Camera is locked ON by the teacher');
      return;
    }
    
    const room = roomRef.current;
    if (!room?.localParticipant || videoToggling) return;
    
    const newState = !videoEnabled;
    
    setHasUserInteracted(prev => ({ ...prev, video: true }));
    setVideoToggling(true);
    setVideoEnabled(newState);
    setUserMutedVideo(!newState);
    
    try {
      if (newState) {
        // When enabling, use the stored device preference
        const savedVideoDevice = localStorage.getItem('simpleclass_video_device');
        const videoConstraints = savedVideoDevice ? { deviceId: { exact: savedVideoDevice } } : undefined;
        try {
          await room.localParticipant.setCameraEnabled(true, videoConstraints);
        } catch (firstError) {
          // AbortError: Timeout starting video source — Chromium 145 bug where the camera
          // driver times out on first acquisition. Retry after a short delay which gives
          // the driver time to finish initialising. This affects both USB and built-in
          // laptop webcams on newer Edge/Chrome versions.
          if (firstError.name === 'AbortError') {
            console.warn('[toggleVideo] AbortError on camera enable, retrying in 2s...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            if (!mountedRef.current) return;
            // Second attempt
            try {
              await room.localParticipant.setCameraEnabled(true, videoConstraints);
            } catch (secondError) {
              if (secondError.name === 'AbortError') {
                console.warn('[toggleVideo] Second attempt also failed, retrying in 3s...');
                await new Promise(resolve => setTimeout(resolve, 3000));
                if (!mountedRef.current) return;
                // Third and final attempt
                await room.localParticipant.setCameraEnabled(true, videoConstraints);
              } else {
                throw secondError;
              }
            }
          } else {
            throw firstError;
          }
        }
      } else {
        await room.localParticipant.setCameraEnabled(false);
      }
    } catch (error) {
      console.error('Failed to toggle camera:', error);
      
      if (mountedRef.current) {
        setVideoEnabled(!newState);
        setUserMutedVideo(newState);
        toast.error(t('liveSession.videoToggleFailed', 'Failed to toggle camera'));
      }
    } finally {
      if (mountedRef.current) {
        setVideoToggling(false);
      }
    }
  }, [videoEnabled, videoToggling, canToggleCamera, roomRef]);

  // FIXED: Single parameter signature matching SessionBottomBar
  const handleToggleMediaLock = useCallback(async (type) => {
    if (!isTeacherUser || !roomRef.current || !mountedRef.current) return;
    
    // Calculate new state based on type
    const newCameraLocked = type === 'camera' ? !cameraLockEnabled : cameraLockEnabled;
    const newMicLocked = type === 'microphone' ? !microphoneLockEnabled : microphoneLockEnabled;
    
    // Optimistic update
    if (type === 'camera') {
      setCameraLockEnabled(newCameraLocked);
    } else {
      setMicrophoneLockEnabled(newMicLocked);
    }
    
    try {
      // Only persist to server - backend will broadcast via WebSocket to all students
      const updateAbortController = new AbortController();
      
      await sessionService.updateMediaRestrictions(sessionId, {
        cameraLocked: newCameraLocked,
        microphoneLocked: newMicLocked
      }, { signal: updateAbortController.signal });
      
      // Show success message
      toast.success(
        type === 'camera'
          ? (newCameraLocked ? 'Camera locked ON for all students' : 'Camera lock removed')
          : (newMicLocked ? 'Microphone locked ON for all students' : 'Microphone lock removed')
      );
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to update media restrictions:', error);
        
        if (mountedRef.current) {
          toast.error('Failed to update media restrictions');
          
          // Rollback on error
          if (type === 'camera') {
            setCameraLockEnabled(!newCameraLocked);
          } else {
            setMicrophoneLockEnabled(!newMicLocked);
          }
        }
      }
    }
  }, [isTeacherUser, sessionId, cameraLockEnabled, microphoneLockEnabled, roomRef]);

  // FIXED: Method to update restrictions from WebSocket - using refs to avoid stale closures
  const updateMediaRestrictions = useCallback(async (restrictions) => {
    if (!mountedRef.current || isTeacherUser) {
      // Teachers ignore their own broadcasts (they update optimistically)
      if (isTeacherUser) {
        console.log('🚫 Teacher ignoring own media restriction broadcast');
      }
      return;
    }
    
    console.log('🔒 Updating media restrictions from WebSocket:', restrictions);
    
    const wasCameraLocked = cameraLockEnabled;
    const wasMicLocked = microphoneLockEnabled;
    
    setCameraLockEnabled(restrictions.cameraLocked || false);
    setMicrophoneLockEnabled(restrictions.microphoneLocked || false);
    
    const room = roomRef.current;
    if (!room?.localParticipant) return;
    
    // Use refs to get CURRENT values, not stale closure values
    const currentVideoEnabled = videoEnabledRef.current;
    const currentAudioEnabled = audioEnabledRef.current;
    
    // FORCE ENABLE camera if locked ON and currently OFF
    if (restrictions.cameraLocked && !currentVideoEnabled) {
      console.log('🎥 Force enabling camera due to lock');
      try {
        setVideoEnabled(true);
        setUserMutedVideo(false);
        const savedVideoDevice = localStorage.getItem('simpleclass_video_device');
        await room.localParticipant.setCameraEnabled(true, savedVideoDevice ? {
          deviceId: { exact: savedVideoDevice }
        } : undefined);
        toast.warning('Teacher has locked your camera ON');
      } catch (error) {
        console.error('Failed to force enable camera:', error);
      }
    } else if (restrictions.cameraLocked && !wasCameraLocked) {
      toast.info('Teacher has locked cameras ON');
    } else if (!restrictions.cameraLocked && wasCameraLocked) {
      toast.info('Camera lock removed');
    }
    
    // FORCE ENABLE microphone if locked ON and currently OFF
    if (restrictions.microphoneLocked && !currentAudioEnabled) {
      console.log('🎤 Force enabling microphone due to lock');
      try {
        setAudioEnabled(true);
        setUserMutedAudio(false);
        const savedAudioDevice = localStorage.getItem('simpleclass_audio_device');
        await room.localParticipant.setMicrophoneEnabled(true, savedAudioDevice ? {
          deviceId: { exact: savedAudioDevice }
        } : undefined);
        toast.warning('Teacher has locked your microphone ON');
      } catch (error) {
        console.error('Failed to force enable microphone:', error);
      }
    } else if (restrictions.microphoneLocked && !wasMicLocked) {
      toast.info('Teacher has locked microphones ON');
    } else if (!restrictions.microphoneLocked && wasMicLocked) {
      toast.info('Microphone lock removed');
    }
  }, [isTeacherUser, cameraLockEnabled, microphoneLockEnabled, roomRef]); // REMOVED videoEnabled, audioEnabled from deps

  // Reset initialization when room changes
  useEffect(() => {
    const currentRoom = roomRef.current;
    
    return () => {
      if (roomRef.current !== currentRoom) {
        hasInitializedRef.current = false;
      }
    };
  }, [roomRef.current]);

  // Main cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      
      cleanupFunctionsRef.current.forEach(cleanup => {
        try {
          cleanup();
        } catch (e) {
          console.debug('Cleanup error:', e);
        }
      });
      cleanupFunctionsRef.current = [];
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      hasInitializedRef.current = false;
    };
  }, []);

  return {
    audioEnabled,
    videoEnabled,
    videoToggling,
    cameraLockEnabled,
    microphoneLockEnabled,
    canToggleCamera,
    canToggleMicrophone,
    toggleAudio,
    toggleVideo,
    handleToggleMediaLock,
    setAudioEnabled,
    setVideoEnabled,
    userMutedAudio,
    userMutedVideo,
    hasUserInteracted,
    updateMediaRestrictions // Export this method
  };
};